import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { importsAPI, crmImportAPI } from '../../lib/api';
import { Layout } from '../../components/Layout';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Loader2, Upload, Check, AlertTriangle, FileSpreadsheet,
  Users, Briefcase, Receipt, FileText, ArrowRight, Database,
} from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';

const TAB_ALL = 'all';
const TAB_CRM = 'crm';
const TAB_INV = 'inventory';

const TABS = [
  { key: TAB_ALL, label: 'All Imports' },
  { key: TAB_CRM, label: 'CRM Migrations' },
  { key: TAB_INV, label: 'Inventory & Catalogue' },
];

const INV_ENTITY_LABELS = {
  inventory_items: 'Inventory Items',
  price_book: 'Price Book',
  vehicles: 'Vehicles',
  warehouses: 'Warehouses',
};

const CRM_ENTITY_ICONS = {
  customers: Users,
  jobs: Briefcase,
  estimates: FileText,
  invoices: Receipt,
};

const CRM_SOURCE_LABELS = {
  hcp: 'Housecall Pro',
  servicetitan: 'ServiceTitan',
  jobber: 'Jobber',
  markate: 'Markate',
  generic: 'CSV',
};

function StatusBadge({ status, errorCount }) {
  if (status === 'rolled_back') {
    return <Badge variant="secondary" className="gap-1">Rolled back</Badge>;
  }
  if (status === 'failed') {
    return <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" /> Failed</Badge>;
  }
  if (errorCount > 0) {
    return <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" /> {errorCount} failed</Badge>;
  }
  if (status === 'completed') {
    return <Badge variant="default" className="gap-1"><Check className="w-3 h-3" /> Complete</Badge>;
  }
  return <Badge variant="outline" className="gap-1">{status}</Badge>;
}

export default function ImportHistory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || TAB_ALL;

  const [invSessions, setInvSessions] = useState([]);
  const [crmSessions, setCrmSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [invRes, crmRes] = await Promise.all([
          importsAPI.listSessions().catch(() => ({ data: [] })),
          crmImportAPI.sessions().then(r => r.data?.sessions || []).catch(() => []),
        ]);

        setInvSessions(
          (invRes.data || []).map(s => ({
            ...s,
            _type: 'inventory',
            _sortDate: s.created_at ? new Date(s.created_at) : new Date(0),
          }))
        );

        setCrmSessions(
          (crmRes || []).map(s => ({
            ...s,
            _type: 'crm',
            _sortDate: s.created_at ? new Date(s.created_at) : new Date(0),
          }))
        );
      } catch {
        toast.error('Failed to load import history');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredSessions = useMemo(() => {
    let list;
    if (activeTab === TAB_CRM) list = crmSessions;
    else if (activeTab === TAB_INV) list = invSessions;
    else list = [...crmSessions, ...invSessions];
    return list.sort((a, b) => b._sortDate - a._sortDate);
  }, [activeTab, crmSessions, invSessions]);

  const setTab = (key) => setSearchParams({ tab: key });

  const emptyMessages = {
    [TAB_ALL]: 'No imports yet. Use the Migration Wizard to import CRM data, or upload CSVs from the Catalogue, Price Book, or Vehicles pages.',
    [TAB_CRM]: 'No CRM migrations yet. Use the Migration Wizard to import customers, jobs, estimates, and invoices from another system.',
    [TAB_INV]: 'No inventory imports yet. Upload a CSV from the Catalogue, Price Book, or Vehicles pages.',
  };

  const renderCrmRow = (s) => {
    const EntityIcon = CRM_ENTITY_ICONS[s.entity_type] || Database;
    const sourceLabel = CRM_SOURCE_LABELS[s.source_system] || s.source_system;
    const entityLabel = s.entity_type ? s.entity_type.charAt(0).toUpperCase() + s.entity_type.slice(1) : 'Import';
    const imported = s.imported_count || 0;
    const errors = s.error_count || 0;

    return (
      <TableRow
        key={`crm-${s.id}`}
        className="cursor-pointer hover:bg-muted/50"
        data-testid={`crm-session-${s.id}`}
      >
        <TableCell>
          <div className="flex items-center gap-2">
            <EntityIcon className="w-4 h-4 text-primary shrink-0" />
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-base font-semibold">{entityLabel}</span>
                <Badge variant="outline" className="text-xs font-normal">{sourceLabel}</Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {s.file_name && <span>{s.file_name} · </span>}
                {s.created_at ? new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
              </div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="secondary" className="text-xs">CRM</Badge>
        </TableCell>
        <TableCell>
          <StatusBadge status={s.status} errorCount={errors} />
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-3 text-sm">
            {imported > 0 && (
              <span className="flex items-center gap-1 text-emerald-600">
                <Check className="w-3.5 h-3.5" /> {imported}
              </span>
            )}
            {errors > 0 && (
              <span className="flex items-center gap-1 text-red-600">
                <AlertTriangle className="w-3.5 h-3.5" /> {errors}
              </span>
            )}
            <span className="text-muted-foreground font-mono text-xs">{s.total_rows} total</span>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const renderInvRow = (s) => (
    <TableRow
      key={`inv-${s.id}`}
      className="cursor-pointer hover:bg-muted/50"
      onClick={() => navigate(`/import-review/${s.id}`)}
      data-testid={`inv-session-${s.id}`}
    >
      <TableCell>
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-muted-foreground shrink-0" />
          <div>
            <span className="text-base font-semibold">{s.filename || 'CSV Import'}</span>
            <div className="text-xs text-muted-foreground mt-0.5">
              {INV_ENTITY_LABELS[s.entity_type] || s.entity_type}
              {s.created_at && ` · ${new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">Inventory</Badge>
      </TableCell>
      <TableCell>
        <StatusBadge status="completed" errorCount={s.failed_count || 0} />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-3 text-sm">
          <span className="flex items-center gap-1 text-emerald-600">
            <Check className="w-3.5 h-3.5" /> {s.success_count}
          </span>
          {(s.failed_count || 0) > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <AlertTriangle className="w-3.5 h-3.5" /> {s.failed_count}
            </span>
          )}
          <span className="text-muted-foreground font-mono text-xs">{s.total_rows} total</span>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <Layout>
      <div className="space-y-6 w-full min-w-0 max-w-full overflow-hidden" data-testid="import-history-page">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="import-history-title">
              <Upload className="w-6 h-6" />
              Import History
            </h1>
            <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
              Review past imports across CRM migrations and inventory uploads
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/migration-wizard')} data-testid="btn-new-migration">
            <ArrowRight className="w-4 h-4 mr-1" /> Migration Wizard
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b" data-testid="import-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
              {t.key === TAB_CRM && crmSessions.length > 0 && (
                <span className="ml-1.5 text-xs bg-primary/10 text-primary rounded-full px-1.5 py-0.5">{crmSessions.length}</span>
              )}
              {t.key === TAB_INV && invSessions.length > 0 && (
                <span className="ml-1.5 text-xs bg-muted rounded-full px-1.5 py-0.5">{invSessions.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Details</TableHead>
                <TableHead className="w-24">Type</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="text-right w-36">Rows</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : filteredSessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    {emptyMessages[activeTab]}
                  </TableCell>
                </TableRow>
              ) : (
                filteredSessions.map(s =>
                  s._type === 'crm' ? renderCrmRow(s) : renderInvRow(s)
                )
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
